import React, { useCallback, useEffect, useState } from "react";
import { ExternalLink, FolderKanban } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import { projectService, apiErrorMessage } from "../../api/taskBoardService";
import CatalogListLayout from "../../components/task-board/CatalogListLayout";
import { ProjectFormModal } from "../../components/task-board/CatalogFormModals";
import { formatDate } from "../../components/task-board/shared";

// Opens in a new tab without triggering the row's navigation.
const UrlCell = ({ url }) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-primary-accent hover:underline max-w-[220px]"
      title={url}
    >
      <span className="truncate">{url.replace(/^https?:\/\//, "")}</span>
      <ExternalLink className="w-3.5 h-3.5 shrink-0" />
    </a>
  ) : (
    <span className="italic text-gray-400 dark:text-light-500">Not set</span>
  );

const Projects = () => {
  const { auth } = useAuth();
  const admin = isAdmin(auth?.employee);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectService.list();
      setProjects(res.data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load projects"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <CatalogListLayout
        title="Projects"
        subtitle="Workspace projects. Their labels appear on tasks and in board filters."
        icon={FolderKanban}
        canCreate={admin}
        createLabel="New project"
        onCreate={() => setCreating(true)}
        loading={loading}
        error={error}
        onRetry={load}
        items={projects}
        emptyTitle="No projects yet"
        emptyMessage={admin ? "Create a project to label and filter tasks by it." : "An admin has not created any projects yet."}
        rowLink={(p) => `/task-board/projects/${p._id}`}
        columns={[
          {
            header: "Project",
            render: (p) => (
              <span className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                <span className="whitespace-nowrap">{p.name}</span>
              </span>
            ),
          },
          {
            header: "Description",
            className: "max-w-xs",
            render: (p) => <span className="block truncate" title={p.description}>{p.description || "—"}</span>,
          },
          { header: "Development", render: (p) => <UrlCell url={p.developmentUrl} /> },
          { header: "Production", render: (p) => <UrlCell url={p.productionUrl} /> },
          { header: "Updated", className: "whitespace-nowrap", render: (p) => formatDate(p.updatedAt) },
        ]}
      />
      <ProjectFormModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          load();
        }}
      />
    </>
  );
};

export default Projects;
